/**
 * SKU Velocity — the React shell around `VelocityScatterEngine`: canvas lifecycle, the band
 * summary strip, the filter banner, and the hover tooltip. The engine owns all drawing; this
 * component only feeds it data and translates DOM events.
 *
 * Reconciliation note (flagged for review): the vertical threshold line is positioned by DIAL
 * VALUE (`thresholdFraction`), while which SKUs actually dim comes from `buildClaimMap`'s real
 * per-family membership (`LeverFamily.memberIds`/`memberStop`). The build handover wants these
 * to be "the same fact drawn twice" — true by construction when a drop's `memberStop` values
 * are assigned by real velocity ordering, approximate otherwise. The band chips above the plot
 * and the lever cards' deltas, by contrast, are both counted straight off the SAME claim map, so
 * THOSE numbers cannot disagree with each other even if the line's exact pixel position is only
 * indicative.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type { LeverCause, ScenarioParams, ScenarioResult, Sku } from "@/lib/data";

import type { Placement, SystemKey } from "./mock";
import { type ScatterColors, VelocityScatterEngine } from "./scatter-engine";
import type { FilterState, ScenariosAction } from "./state";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

const BAND_COLOR: ScatterColors = {
  A: "#4A423C",
  B: "#3F6D8C",
  C: "#8C837B",
  D: "#EB1700",
  dim: "#C1BBB3",
  line: "#E8E6E3",
  muted: "#81766F",
  red: "#EB1700",
  ink: "#1A1512",
};

const SYS_NAME: Record<SystemKey, string> = {
  aframe: "A-Frame",
  p2l: "Pick-to-Light",
  perfect: "Perfect Pick",
};

interface HoverState {
  sku: Sku;
  cause: LeverCause;
  system?: string;
  misslot?: boolean;
  x: number;
  y: number;
}

export function VelocityScatter({
  skus,
  placements,
  draft,
  result,
  filter,
  dispatch,
}: {
  skus: readonly Sku[];
  placements: ReadonlyMap<number, Placement>;
  draft: ScenarioParams;
  result: ScenarioResult;
  filter: FilterState;
  dispatch: (a: ScenariosAction) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<VelocityScatterEngine | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const invMax = useMemo(() => skus.reduce((a, s) => Math.max(a, s.inv), 1), [skus]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || skus.length === 0) {
      return;
    }
    const engine = new VelocityScatterEngine(canvas);
    engine.setPopulation(skus, invMax);
    engineRef.current = engine;
    const ro = new ResizeObserver(() => {
      engine.resize();
      repaint();
    });
    if (wrapRef.current) {
      ro.observe(wrapRef.current);
    }
    return () => ro.disconnect();
  }, [skus, invMax]);

  function repaint() {
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    engine.paint({
      claim: result.claim,
      filter,
      placements,
      reloThreshold: draft.reloThreshold,
      colors: BAND_COLOR,
    });
  }

  useEffect(() => {
    repaint();
  }, [result.claim, filter, draft.reloThreshold]);

  const bandOrder: Array<"D" | "C" | "B" | "A"> = ["D", "C", "B", "A"];

  return (
    <div className="section" id="sec-scatter" data-spot="scatter">
      <div className="section-head">
        <div className="section-title">SKU Velocity</div>
      </div>
      <div className="section-body">
        {filter ? (
          <div className="filter-row on">
            Showing <b>{fmt(filteredCount(skus, placements, filter))}</b> SKUs{" "}
            {filter.mis ? "mis-slotted in" : "held in"} <b>{SYS_NAME[filter.sys]}</b>
            <button
              className="filter-clear"
              onClick={() => dispatch({ type: "filter", filter: null })}
            >
              Clear filter
            </button>
          </div>
        ) : null}
        <div className="band-strip">
          {bandOrder.map((k) => {
            const claimed = result.bandClaimed[k];
            const gone = claimed.relo + claimed.pack + claimed.conv;
            const now = result.bandCounts[k] - gone;
            return (
              <div className="bstat" key={k} style={{ flex: k === "D" ? "1 1 0" : "3 1 0" }}>
                <span className="bl">{k}</span>
                <span className="bn">{fmt(now)}</span>
                <span className={gone > 0 ? "bg on" : "bg"}>
                  {gone > 0 ? `− ${fmt(gone)}` : "held"}
                </span>
              </div>
            );
          })}
        </div>
        <div className="scatter-canvas-wrap" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            onMouseMove={(e) => {
              const engine = engineRef.current;
              if (!engine) {
                return;
              }
              const rect = e.currentTarget.getBoundingClientRect();
              const mx = e.clientX - rect.left;
              const my = e.clientY - rect.top;
              const hit = engine.hitTest(mx, my, placements, result.claim);
              setHover(hit ? { ...hit, x: e.clientX, y: e.clientY } : null);
            }}
            onMouseLeave={() => setHover(null)}
          />
        </div>
      </div>
      {hover ? (
        <div
          className="tt"
          style={{
            left: Math.min(window.innerWidth - 250, hover.x + 14),
            top: hover.y + 12,
            display: "block",
          }}
        >
          <b>{hover.sku.name}</b>
          <br />
          band {hover.sku.band} · <b>{hover.sku.vel}</b> boxes/mo · <b>{fmt(hover.sku.inv)}</b>{" "}
          units
          <br />
          sits in {hover.system ? SYS_NAME[hover.system as SystemKey] : "—"}
          {hover.misslot ? <span className="mis-t"> · mis-slotted</span> : null}
          {hover.cause ? (
            <>
              <br />
              claimed by{" "}
              {hover.cause === "relo"
                ? "relocate"
                : hover.cause === "pack"
                  ? "pack rationalize"
                  : "Rx→Dx convert"}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function filteredCount(
  skus: readonly Sku[],
  placements: ReadonlyMap<number, Placement>,
  filter: NonNullable<FilterState>,
): number {
  let n = 0;
  for (const s of skus) {
    const p = placements.get(s.id);
    if (p && p.system === filter.sys && (!filter.mis || p.misslot)) {
      n++;
    }
  }
  return n;
}
